import { useEffect, useState, useRef, useCallback } from 'react';
import { useSolanaLive } from './useSolanaLive';
import { fetchWalletSolBalance } from '../solana/fetchers/walletSolBalance';
import { fetchCurrentRoundTokenBalance } from '../solana/fetchers/currentRoundTokenBalance';

function createEmptyBalances() {
  return {
    solBalance: '0',
    solLamports: 0,
    roundTokenBalance: '0',
    roundTokenAmountAtomic: '0',
    roundTokenAta: '',
  };
}

async function loadUserBalances(walletPublicKey, currentGameState) {
  if (!walletPublicKey) {
    return createEmptyBalances();
  }

  const [walletSolBalance, roundTokenBalance] = await Promise.all([
    fetchWalletSolBalance(walletPublicKey),
    fetchCurrentRoundTokenBalance(walletPublicKey, currentGameState),
  ]);

  return {
    solBalance: walletSolBalance.solDisplay,
    solLamports: walletSolBalance.lamports,
    roundTokenBalance: roundTokenBalance.displayAmount,
    roundTokenAmountAtomic: roundTokenBalance.amountAtomic,
    roundTokenAta: roundTokenBalance.ataPubkey,
  };
}

export function useUserBalances(walletPublicKey) {
  const { currentGameState } = useSolanaLive();
  const [balances, setBalances] = useState(createEmptyBalances);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Keep a ref to always access the latest currentGameState for manual refreshes
  const currentGameStateRef = useRef(currentGameState);
  currentGameStateRef.current = currentGameState;

  const refreshBalances = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      // Use the ref to get the latest currentGameState
      const nextBalances = await loadUserBalances(walletPublicKey, currentGameStateRef.current);

      setBalances(nextBalances);
      return nextBalances; // Return the new balance values for immediate use
    } catch (loadError) {
      setError(loadError?.message || 'Unable to load user balances.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [walletPublicKey]);

  // Only refetch when wallet changes or game_id changes (new game starts)
  // Do NOT refetch on every currentGameState update (reserve, supply, etc.)
  const currentGameId = currentGameState?.game_id || null;

  useEffect(() => {
    let cancelled = false;

    const syncBalances = async () => {
      setLoading(true);
      setError('');

      try {
        const nextBalances = await loadUserBalances(walletPublicKey, currentGameStateRef.current);

        if (!cancelled) {
          setBalances(nextBalances);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError?.message || 'Unable to load user balances.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    syncBalances();

    return () => {
      cancelled = true;
    };
  }, [walletPublicKey, currentGameId]);

  return {
    solBalance: balances.solBalance,
    solLamports: balances.solLamports,
    roundTokenBalance: balances.roundTokenBalance,
    roundTokenAmountAtomic: balances.roundTokenAmountAtomic,
    roundTokenAta: balances.roundTokenAta,
    loading,
    error,
    refreshBalances,
  };
}